import { CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { getSafely } from '@/shared/safely'

import { CompRatingProps } from './comp'

export interface CompRatingWrapperProps {
  infos: CoreComponentInfos
  children: (props: CompRatingProps) => React.ReactNode
}

/**
 * CompRatingWrapperCore
 */
export const CompRatingWrapperCore = ({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  children,
}: CompRatingWrapperProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const ratingValue = useParseVariableSelect<string>(
    settings[ComponentSettingKey.RatingDataValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const ratingValueNumber = isNaN(Number(ratingValue)) ? 0 : Number(ratingValue)
  const ratingCount = getSafely(settings, [ComponentSettingKey.RatingDataCount], [5])[0]
  const ratingInterval = getSafely(settings, [ComponentSettingKey.RatingDataInterval], [1])[0] as 0.5 | 1
  const ratingSize = getSafely(settings, [ComponentSettingKey.RatingDesignSize], 'md') as 'sm' | 'md' | 'lg' | 'xl'

  // ========================= design =========================

  // base
  const alignment = settings[ComponentSettingKey.RatingDesignHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.RatingDesignMargin] as SpacingInputValue

  // color
  const ratingColor = settings[ComponentSettingKey.RatingDesignColor]
  const ratingColorSolid = settings[ComponentSettingKey.RatingDesignColorSolid] || 'primary-40'
  const ratingProgressColor = settings[ComponentSettingKey.RatingDesignProgressColor]

  const ratingVariant = settings[ComponentSettingKey.RatingDesignVariant] ?? 'default'
  const ratingWidth = settings[ComponentSettingKey.RatingDesignWidth] ?? 100

  const ratingText = useParseVariableText(
    settings[ComponentSettingKey.RatingDataText],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const rating1Start = settings[ComponentSettingKey.RatingData1Start] ?? 0
  const rating2Start = settings[ComponentSettingKey.RatingData2Start] ?? 0
  const rating3Start = settings[ComponentSettingKey.RatingData3Start] ?? 0
  const rating4Start = settings[ComponentSettingKey.RatingData4Start] ?? 0
  const rating5Start = settings[ComponentSettingKey.RatingData5Start] ?? 0

  let ratingDistribution: { stars: number; percentage: number }[] = []
  if (
    rating5Start !== undefined ||
    rating4Start !== undefined ||
    rating3Start !== undefined ||
    rating2Start !== undefined ||
    rating1Start !== undefined
  ) {
    ratingDistribution = []
    if (rating5Start !== undefined) {
      ratingDistribution.push({ stars: 5, percentage: rating5Start })
    }
    if (rating4Start !== undefined) {
      ratingDistribution.push({ stars: 4, percentage: rating4Start })
    }
    if (rating3Start !== undefined) {
      ratingDistribution.push({ stars: 3, percentage: rating3Start })
    }
    if (rating2Start !== undefined) {
      ratingDistribution.push({ stars: 2, percentage: rating2Start })
    }
    if (rating1Start !== undefined) {
      ratingDistribution.push({ stars: 1, percentage: rating1Start })
    }
  }

  // ========================= render =========================
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const ratingProps: CompRatingProps = {
    id: component._id,
    alignment,
    margin,
    backgroundColor: currentParentBg || 'primary-95',

    //
    value: ratingValueNumber,
    count: ratingCount,
    interval: ratingInterval,
    size: ratingSize,
    readOnly: true,
    iconColorType: ratingColor,
    iconColorSolid: ratingColorSolid,
    progressBarColor: ratingProgressColor,
    text: ratingText,
    ratingDistribution,
    variant: ratingVariant,
    width: ratingWidth,
    devMode: isDevelopmentMode,
    onClickDev: handleClick,
  }

  return children(ratingProps)
}
